import React, { useEffect, useState } from 'react';
import { Box, LinearProgress, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  InsertDriveFile,
  AttachFile,
  Chat as ChatIcon,
  Event as EventIcon,
  EventAvailable,
  Insights,
  Notifications as NotificationsIcon,
  Article,
  Straighten,
} from '@mui/icons-material';
import { ResourceUsage, ResourceUsageItem, ResourceUsageSummary } from '@/types/resource-usage';

type ColorFamily = 'info' | 'secondary' | 'success' | 'warning' | 'primary' | 'error';
type Shade = 'light' | 'main' | 'dark';

interface CategoryConfig {
  label: string;
  icon: React.ReactNode;
  colorFamily: ColorFamily;
  shade: Shade;
  format: (value: number) => string;
}

const formatBytes = (bytes: number): string => bytes > 0 ? `${(bytes / 1024 / 1024).toFixed(1)} МБ` : '0 МБ';
const formatCount = (count: number): string => String(count);

// colorFamily+shade (не лише шість іменованих кольорів MUI) — щоб вистачило унікальних
// комбінацій на всі категорії, без хардкоду hex (тільки theme.palette-токени).
const CATEGORY_CONFIG: Record<keyof ResourceUsage, CategoryConfig> = {
  files_manager: { label: 'Файли', icon: <InsertDriveFile sx={{ fontSize: 13 }} />, colorFamily: 'info', shade: 'main', format: formatBytes },
  files_chat: { label: 'Файли в чаті', icon: <AttachFile sx={{ fontSize: 13 }} />, colorFamily: 'info', shade: 'dark', format: formatBytes },
  chat_messages: { label: 'Повідомлення', icon: <ChatIcon sx={{ fontSize: 13 }} />, colorFamily: 'success', shade: 'main', format: formatCount },
  calendar_events: { label: 'Події', icon: <EventIcon sx={{ fontSize: 13 }} />, colorFamily: 'warning', shade: 'main', format: formatCount },
  meetings: { label: 'Зустрічі', icon: <EventAvailable sx={{ fontSize: 13 }} />, colorFamily: 'secondary', shade: 'main', format: formatCount },
  ai_reports: { label: 'AI-звіти', icon: <Insights sx={{ fontSize: 13 }} />, colorFamily: 'success', shade: 'dark', format: formatCount },
  notifications: { label: 'Сповіщення', icon: <NotificationsIcon sx={{ fontSize: 13 }} />, colorFamily: 'primary', shade: 'main', format: formatCount },
  expert_posts: { label: 'Пости', icon: <Article sx={{ fontSize: 13 }} />, colorFamily: 'secondary', shade: 'dark', format: formatCount },
  body_measurements: { label: 'Метрики', icon: <Straighten sx={{ fontSize: 13 }} />, colorFamily: 'warning', shade: 'dark', format: formatCount },
};

interface Props {
  id: number;
  fetchUsage: (id: number) => Promise<ResourceUsageSummary>;
}

/**
 * Компактна міні-діаграма використання ресурсів користувача в одній комірці таблиці.
 * Сама довантажує дані незалежно за допомогою переданого `fetchUsage` (без участі
 * батьківської сторінки) — дозволяє перевикористовувати компонент і в адмінці
 * (admin-resource-usage.service), і на сторінці клієнта для експерта (client-resource-usage.service).
 * Шкала кожного бару — реальний threshold з бекенду (resource-usage модуль), не вигадане число.
 */
export const ResourceUsageCell: React.FC<Props> = ({ id, fetchUsage }) => {
  const theme = useTheme();
  const [summary, setSummary] = useState<ResourceUsageSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setSummary(null);
    setLoading(true);
    fetchUsage(id)
      .then(setSummary)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id, fetchUsage]);

  if (loading) {
    return <LinearProgress sx={{ width: 90, height: 4, borderRadius: 2 }} />;
  }

  if (!summary) {
    return <>—</>;
  }

  const usage = summary.categories;
  const activeKeys = (Object.keys(CATEGORY_CONFIG) as (keyof ResourceUsage)[]).filter((key) => usage[key].value > 0);

  if (activeKeys.length === 0) {
    return <Typography variant="caption" color="text.disabled">Немає активності</Typography>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 130 }}>
      {activeKeys.map((key) => {
        const cfg = CATEGORY_CONFIG[key];
        const item: ResourceUsageItem = usage[key];
        const percent = Math.min((item.value / item.threshold) * 100, 100);
        const resolvedColor = item.exceeded ? theme.palette.error.main : theme.palette[cfg.colorFamily][cfg.shade];
        return (
          <Tooltip
            key={key}
            title={`${cfg.label}: ${cfg.format(item.value)} з ${cfg.format(item.threshold)}${item.exceeded ? ' — перевищено' : ''}`}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ display: 'flex', color: resolvedColor }}>{cfg.icon}</Box>
              <LinearProgress
                variant="determinate"
                value={percent}
                sx={{
                  flexGrow: 1,
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: alpha(resolvedColor, 0.08),
                  '& .MuiLinearProgress-bar': { backgroundColor: resolvedColor },
                }}
              />
              <Typography variant="caption" sx={{ minWidth: 38, textAlign: 'right', color: resolvedColor }}>
                {cfg.format(item.value)}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};
